/**
 * ============================================================================
 * TRANSACTIONS PAGE - List All Transactions
 * ============================================================================
 *
 * Green/black theme matching sidebar and dashboard – hero, bento stats, themed cards
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTransaction } from '../hooks';
import { useTransactionStore } from '../store';
import { useToast } from '../providers';
import { useI18n } from '../providers';
import { UserDashboardLayout } from '../components/user';
import TransactionCard from '../components/transaction/TransactionCard';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Spinner from '../components/common/Spinner';
import '../styles/landing.css';
import '../styles/animations.css';
import '../styles/global.css';

const Transactions = () => {
  const navigate = useNavigate();
  const { loadTransactions, loading, transactions } = useTransaction();
  const { filters, setFilters, pagination, setPagination } = useTransactionStore();
  const { showError } = useToast();
  const { language } = useI18n();

  const [searchQuery, setSearchQuery] = useState(filters?.search || '');
  const [isLoading, setIsLoading] = useState(true);
  const [statsInView, setStatsInView] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        await loadTransactions({
          ...filters,
          search: searchQuery || undefined,
          page: pagination.page,
          limit: pagination.pageSize,
        });
      } catch (error) {
        showError('Failed to load transactions');
        console.error('Load transactions error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [filters, pagination.page, pagination.pageSize, searchQuery, loadTransactions, showError]);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => e.isIntersecting && setStatsInView(true)); },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, search: searchQuery });
    setPagination({ ...pagination, page: 1 });
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, page: 1 });
  };

  const clearFilters = () => {
    setFilters({ search: null, status: null });
    setSearchQuery('');
    setPagination({ ...pagination, page: 1 });
  };

  const statusOptions = [
    { value: '', label: language === 'fr' ? 'Tous les statuts' : 'All Statuses' },
    { value: 'pending', label: language === 'fr' ? 'En attente' : 'Pending' },
    { value: 'payment_received', label: language === 'fr' ? 'Paiement reçu' : 'Payment Received' },
    { value: 'delivered', label: language === 'fr' ? 'Livré' : 'Delivered' },
    { value: 'completed', label: language === 'fr' ? 'Terminé' : 'Completed' },
    { value: 'cancelled', label: language === 'fr' ? 'Annulé' : 'Cancelled' },
    { value: 'disputed', label: language === 'fr' ? 'En litige' : 'Disputed' },
  ];

  const list = Array.isArray(transactions) ? transactions : [];
  const stats = {
    total: list.length,
    active: list.filter((t) => ['escrow_funded', 'in_progress', 'delivered'].includes(t?.status)).length,
    completed: list.filter((t) => t?.status === 'completed').length,
    disputed: list.filter((t) => t?.status === 'disputed').length,
  };

  const totalFromPagination = pagination?.total ?? list.length;
  const showPagination = totalFromPagination > (pagination?.pageSize ?? 10);

  if (isLoading || loading) {
    return (
      <UserDashboardLayout hideFooter>
        <div className="transactions-wt transactions-wt-loading">
          <div className="dashboard-wt-hero-inner">
            <Spinner size="lg" />
            <p className="mt-4 text-[#a3a3a3]">
              {language === 'fr' ? 'Chargement des transactions...' : 'Loading transactions...'}
            </p>
          </div>
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div className="transactions-wt dashboard-wt">
        {/* Hero */}
        <section className="dashboard-wt-hero">
          <div className="dashboard-wt-hero-bg" aria-hidden="true" />
          <div className="dashboard-wt-hero-grid" aria-hidden="true" />
          <div className="dashboard-wt-hero-noise" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-1" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-2" aria-hidden="true" />

          <div className="dashboard-wt-hero-inner">
            <h1 className="dashboard-wt-hero-title">
              💳 {language === 'fr' ? 'Mes Transactions' : 'My Transactions'}
            </h1>
            <p className="dashboard-wt-hero-sub">
              {language === 'fr'
                ? 'Gérez toutes vos transactions et suivez leur progression'
                : 'Manage all your transactions and track their progress'}
            </p>
            <button
              type="button"
              className="dashboard-wt-btn-primary dashboard-wt-btn-large mt-6"
              onClick={() => navigate('/transactions/create')}
            >
              <span className="dashboard-wt-btn-icon">+</span>
              {language === 'fr' ? 'Nouvelle transaction' : 'New Transaction'}
            </button>
          </div>
        </section>

        {/* Stats - Bento */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className={`dashboard-wt-stats ${statsInView ? 'is-inview' : ''}`} ref={statsRef}>
              {[
                { icon: '📊', label: language === 'fr' ? 'Total' : 'Total', value: stats.total },
                { icon: '🔄', label: language === 'fr' ? 'Actives' : 'Active', value: stats.active },
                { icon: '✅', label: language === 'fr' ? 'Complétées' : 'Completed', value: stats.completed },
                { icon: '⚠️', label: language === 'fr' ? 'En litige' : 'Disputed', value: stats.disputed },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="dashboard-wt-stat-card"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="dashboard-wt-stat-icon">
                    <span>{stat.icon}</span>
                  </div>
                  <div className="dashboard-wt-stat-content">
                    <div className="dashboard-wt-stat-value">{stat.value}</div>
                    <div className="dashboard-wt-stat-label">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="dashboard-wt-section dashboard-wt-section-alt">
          <div className="dashboard-wt-section-inner">
            <div className="transactions-wt-filters dashboard-wt-card">
              <div className="dashboard-wt-card-header">
                <h2 className="dashboard-wt-card-title">
                  {language === 'fr' ? 'Filtres' : 'Filters'}
                </h2>
              </div>
              <div className="dashboard-wt-card-content">
                <form onSubmit={handleSearch} className="transactions-wt-form">
                  <div className="transactions-wt-form-row">
                    <div className="transactions-wt-form-search">
                      <Input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
                        className="transactions-wt-input"
                      />
                    </div>
                    <div className="transactions-wt-form-select">
                      <Select
                        value={filters?.status || ''}
                        onChange={(e) => handleFilterChange('status', e.target.value || null)}
                        options={statusOptions}
                      />
                    </div>
                    <button type="submit" className="dashboard-wt-btn-primary">
                      {language === 'fr' ? 'Rechercher' : 'Search'}
                    </button>
                    {(filters?.search || filters?.status) && (
                      <button
                        type="button"
                        className="dashboard-wt-btn-secondary"
                        onClick={clearFilters}
                      >
                        {language === 'fr' ? 'Effacer' : 'Clear'}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* List or empty */}
            {list.length === 0 ? (
              <div className="dashboard-wt-card dashboard-wt-empty">
                <div className="dashboard-wt-empty-icon">💳</div>
                <h3 className="dashboard-wt-empty-title">
                  {language === 'fr' ? 'Aucune transaction' : 'No transactions found'}
                </h3>
                <p className="dashboard-wt-empty-desc">
                  {language === 'fr'
                    ? 'Commencez par créer votre première transaction'
                    : 'Get started by creating your first transaction'}
                </p>
                <button
                  type="button"
                  className="dashboard-wt-btn-primary dashboard-wt-btn-large"
                  onClick={() => navigate('/transactions/create')}
                >
                  {language === 'fr' ? 'Créer une transaction' : 'Create Your First Transaction'}
                </button>
              </div>
            ) : (
              <>
                <div className="dashboard-wt-transactions transactions-wt-list">
                  {list.map((transaction, index) => (
                    <div
                      key={transaction.id}
                      className="dashboard-wt-transaction-wrapper"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <TransactionCard
                        transaction={transaction}
                        onClick={() => navigate(`/transactions/${transaction.id}`)}
                      />
                    </div>
                  ))}
                </div>

                {showPagination && (
                  <div className="transactions-wt-pagination dashboard-wt-card">
                    <div className="transactions-wt-pagination-inner">
                      <span className="transactions-wt-pagination-text">
                        {language === 'fr' ? 'Affichage' : 'Showing'}{' '}
                        {(pagination.page - 1) * (pagination.pageSize || 10) + 1}{' '}
                        {language === 'fr' ? 'à' : 'to'}{' '}
                        {Math.min(pagination.page * (pagination.pageSize || 10), totalFromPagination)}{' '}
                        {language === 'fr' ? 'sur' : 'of'} {totalFromPagination}
                      </span>
                      <div className="transactions-wt-pagination-btns">
                        <button
                          type="button"
                          className="dashboard-wt-btn-secondary"
                          onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                          disabled={pagination.page <= 1}
                        >
                          {language === 'fr' ? 'Précédent' : 'Previous'}
                        </button>
                        <button
                          type="button"
                          className="dashboard-wt-btn-secondary"
                          onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                          disabled={pagination.page * (pagination.pageSize || 10) >= totalFromPagination}
                        >
                          {language === 'fr' ? 'Suivant' : 'Next'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </UserDashboardLayout>
  );
};

export default Transactions;
